/**
 * Plan-change detection for the bulk editor.
 *
 * A fresh session per USER INSTRUCTION was tried and rolled back: it cost
 * multi-turn refinement ("and also cut the budget 10%" started over), and the
 * staleness it was really guarding against has two better-targeted answers —
 * a session scoped to the plan being edited (the Copy Adjust grid keys its
 * session on the NEW copy's row, so every copy request is a fresh session by
 * construction), and this fingerprint.
 */

/**
 * A fingerprint of WHICH rows exist, for detecting that the plan changed under
 * the agent — the cause of it arguing "I count 5 ad groups, not 4" from a digest
 * read several turns earlier. Row identity only, not values, so typing in a cell
 * does not reset a live conversation while adding or deleting a row does.
 * Order-insensitive, because a re-render can reorder rows without changing the
 * plan.
 */
export function rowsFingerprint(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return '';
  return rows.map((r) => String(r?.rowId ?? '')).sort().join('|');
}

export default { rowsFingerprint };

/**
 * Is this chat message a PLAIN approval of whatever is on screen — and
 * nothing else? Deliberately conservative: any qualifier ("yes but…",
 * "apply the first one") fails the match and rides to the agent as ordinary
 * conversation. Used by the bulk editor to let a typed "yes apply" count as
 * the explicit user approval of the visible preview card — without it, typed
 * confirmations bounced off the approval gate forever ("didn't register as
 * approved") while the button sat unclicked (UAT 0819).
 */
const PLAIN_APPROVAL_RE = new RegExp(
  '^(yes|yep|yeah|ok|okay|sure|confirm|confirmed|approve|approved|apply'
  + '|go ahead|do it|proceed|execute|stage it|apply it|confirm it|ship it'
  + '|yes please|please do|yes apply|yes confirm|yes go ahead|apply both'
  + '|yes apply both|confirm both|yes do it)[.! ]*$',
  'i',
);

export function isPlainApproval(text) {
  const t = String(text || '').trim();
  if (!t || t.length > 40) return false;
  return PLAIN_APPROVAL_RE.test(t);
}
